import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { Headset, HeadsetActivationCode } from 'src/models';
import {
  fetchHeadsetActivationCodeAction,
  fetchHeadsetsAction,
  fetchHeadsetAction,
  saveHeadsetAction,
  deleteHeadsetAction,
  deleteCustomerSessionAction,
  activateHeadsetSubscriptionAction,
  cancelHeadsetActiveSubscriptionAction,
  cancelHeadsetActiveSessionAction,
  fetchHeadsetPaymentsAction,
  fetchHeadsetSubscriptionsAction,
  fetchHeadsetUserEventsAction,
  fetchHeadsetEventsAction,
  fetchHeadsetCommentsAction,
  fetchHeadsetMetaAction,
  addHeadsetCommentAction,
  deleteHeadsetCommentAction,
} from 'src/services/headsets';

const headsetsEntityAdapter = createEntityAdapter<Headset>({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  selectId: (headset: Headset) => headset.id,
});

export const headsetsSelectors = headsetsEntityAdapter.getSelectors();

const initialState: {
  uuid: string | null;
  activationCode: HeadsetActivationCode | null;
  isPending: boolean | null;
  hasError: boolean;
} = {
  uuid: null,
  activationCode: null,
  isPending: null,
  hasError: false,
};

const headsetsSlice = createSlice({
  name: 'headsets',
  initialState: headsetsEntityAdapter.getInitialState(initialState),
  reducers: {
    clearHeadsetsState: (state) => {
      state.activationCode = null;
      state.isPending = null;
      state.hasError = false;
      state.uuid = null;
      headsetsEntityAdapter.setAll(state, []);
    },
    clearActivationCode: (state) => {
      state.activationCode = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHeadsetActivationCodeAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetActivationCodeAction.fulfilled,
      (state, { payload }) => {
        state.activationCode = payload;
        state.hasError = false;
      },
    );
    builder.addCase(fetchHeadsetActivationCodeAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(
      fetchHeadsetsAction.pending,
      (
        state,
        {
          meta: {
            arg: { withoutIndication, uuid },
          },
        },
      ) => {
        if (!withoutIndication) {
          state.isPending = true;
        }
        state.hasError = false;
        state.uuid = uuid;
      },
    );
    builder.addCase(
      fetchHeadsetsAction.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { withoutIndication, uuid },
          },
        },
      ) => {
        if (state.uuid === uuid) {
          headsetsEntityAdapter.setAll(state, payload);
          state.hasError = false;
          if (!withoutIndication) {
            state.isPending = false;
          }
        }
      },
    );
    builder.addCase(
      fetchHeadsetsAction.rejected,
      (
        state,
        {
          meta: {
            arg: { withoutIndication },
          },
        },
      ) => {
        if (!withoutIndication) {
          state.isPending = false;
        }
        state.hasError = true;
      },
    );

    builder.addCase(activateHeadsetSubscriptionAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      activateHeadsetSubscriptionAction.fulfilled,
      (state, { meta, payload }) => {
        state.hasError = false;

        const headset = headsetsSelectors.selectById(
          state,
          meta.arg.headsetId,
        ) as Headset;

        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: {
            activeSubscriptions: [...headset.activeSubscriptions, payload],
          },
        });
      },
    );
    builder.addCase(activateHeadsetSubscriptionAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(cancelHeadsetActiveSubscriptionAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      cancelHeadsetActiveSubscriptionAction.fulfilled,
      (state, { meta }) => {
        state.hasError = false;
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { activeSubscriptions: [] },
        });
      },
    );
    builder.addCase(cancelHeadsetActiveSubscriptionAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(fetchHeadsetAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(fetchHeadsetAction.fulfilled, (state, { payload }) => {
      state.hasError = false;
      headsetsEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(fetchHeadsetAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(saveHeadsetAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(saveHeadsetAction.fulfilled, (state, { meta, payload }) => {
      headsetsEntityAdapter.updateOne(state, {
        id: meta.arg.headsetId,
        changes: payload,
      });
      state.hasError = false;
    });
    builder.addCase(saveHeadsetAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(deleteHeadsetAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(deleteHeadsetAction.fulfilled, (state, { meta }) => {
      headsetsEntityAdapter.removeOne(state, meta.arg.headsetId);
      state.hasError = false;
    });
    builder.addCase(deleteHeadsetAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(cancelHeadsetActiveSessionAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      cancelHeadsetActiveSessionAction.fulfilled,
      (state, { meta }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { hasActiveSession: false },
        });
        state.hasError = false;
      },
    );
    builder.addCase(cancelHeadsetActiveSessionAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(fetchHeadsetSubscriptionsAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetSubscriptionsAction.fulfilled,
      (state, { meta, payload }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: {
            subscriptions: payload,
          },
        });
        state.hasError = false;
      },
    );
    builder.addCase(fetchHeadsetSubscriptionsAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(fetchHeadsetPaymentsAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetPaymentsAction.fulfilled,
      (state, { meta, payload }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { invoices: payload },
        });
        state.hasError = false;
      },
    );
    builder.addCase(fetchHeadsetPaymentsAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(fetchHeadsetEventsAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetEventsAction.fulfilled,
      (state, { meta, payload }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: {
            events: payload.events,
            eventTypes: payload.eventTypes,
          },
        });
        state.hasError = false;
      },
    );
    builder.addCase(fetchHeadsetEventsAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(fetchHeadsetUserEventsAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetUserEventsAction.fulfilled,
      (state, { meta, payload }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { userEvents: payload },
        });
        state.hasError = false;
      },
    );
    builder.addCase(fetchHeadsetUserEventsAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(fetchHeadsetCommentsAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetCommentsAction.fulfilled,
      (state, { meta, payload }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { comments: payload },
        });
        state.hasError = false;
      },
    );
    builder.addCase(fetchHeadsetCommentsAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(addHeadsetCommentAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      addHeadsetCommentAction.fulfilled,
      (state, { meta, payload }) => {
        const headset = headsetsSelectors.selectById(
          state,
          meta.arg.headsetId,
        ) as Headset;

        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { comments: [payload, ...headset.comments] },
        });
        state.hasError = false;
      },
    );
    builder.addCase(addHeadsetCommentAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(deleteHeadsetCommentAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(deleteHeadsetCommentAction.fulfilled, (state, { meta }) => {
      const headset = headsetsSelectors.selectById(
        state,
        meta.arg.headsetId,
      ) as Headset;
      headsetsEntityAdapter.updateOne(state, {
        id: meta.arg.headsetId,
        changes: {
          comments: headset.comments.filter((c) => c.id !== meta.arg.commentId),
        },
      });
      state.hasError = false;
    });
    builder.addCase(deleteHeadsetCommentAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(fetchHeadsetMetaAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetMetaAction.fulfilled,
      (state, { meta, payload }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { meta: payload },
        });
        state.hasError = false;
      },
    );
    builder.addCase(fetchHeadsetMetaAction.rejected, (state) => {
      state.hasError = true;
    });

    builder.addCase(deleteCustomerSessionAction.pending, (state) => {
      state.hasError = false;
    });
    builder.addCase(
      deleteCustomerSessionAction.fulfilled,
      (state, { meta, payload }) => {
        state.hasError = false;
        if (payload.result) {
          headsetsEntityAdapter.updateOne(state, {
            id: meta.arg.headsetId,
            changes: {
              hotelCustomerSession: null,
            },
          });
        }
      },
    );
    builder.addCase(deleteCustomerSessionAction.rejected, (state) => {
      state.hasError = true;
    });
  },
});

export const { clearHeadsetsState, clearActivationCode } =
  headsetsSlice.actions;

const { reducer } = headsetsSlice;

export default reducer;
